import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Sparkles, AlertTriangle, Briefcase, Hourglass, CheckCircle2, HelpCircle, RefreshCw, Loader2,
  ChevronDown, ChevronUp, ExternalLink, Calendar, Search, Archive, ArchiveRestore, Trash2, ClipboardList,
  Eye, EyeOff, FileSpreadsheet, FileText, Brush, Copy, ChevronLeft, ChevronRight, X
} from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import { Button } from '../components/ui/button';
import { expedientesAPI } from '../api';
import EncargosIAPanel, { BorradoresIAPanel } from './EncargosIAPanel';

const ESTILOS = {
  'Urgente': { icon: AlertTriangle, chip: 'bg-red-100 text-red-800 border-red-200', barra: 'border-l-red-500', punto: 'bg-red-500' },
  'Acción del despacho': { icon: Briefcase, chip: 'bg-amber-100 text-amber-900 border-amber-200', barra: 'border-l-amber-500', punto: 'bg-amber-500' },
  'Esperando a terceros': { icon: Hourglass, chip: 'bg-blue-100 text-blue-800 border-blue-200', barra: 'border-l-blue-500', punto: 'bg-blue-500' },
  'Sin acción': { icon: CheckCircle2, chip: 'bg-gray-100 text-gray-700 border-gray-200', barra: 'border-l-gray-400', punto: 'bg-gray-400' },
  'Sin clasificar': { icon: HelpCircle, chip: 'bg-slate-100 text-slate-700 border-slate-200', barra: 'border-l-slate-300', punto: 'bg-slate-300' },
};

const estiloDe = (p) => ESTILOS[p] || ESTILOS['Sin clasificar'];
const slug = (p) => (p || '').replace(/\s+/g, '-').toLowerCase();

const fechaCorta = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch (e) { return iso; }
};

const FILTROS_INICIALES = {
  buscar: '', prioridad: '', veredicto: '', dias: 30, archivadas: 'no',
  solo_no_vistas: false, orden: 'prioridad', pagina: 1, por_pagina: 50,
};

const TarjetaAuditoria = ({ item, seleccionada, onSeleccionar, onMarcar, onBorrar, ocupado }) => {
  const [abierta, setAbierta] = useState(false);
  const est = estiloDe(item.prioridad);

  return (
    <div
      className={`bg-white rounded-lg border border-l-4 ${est.barra} shadow-sm hover:shadow-md transition-shadow duration-200 ${item.archivada ? 'opacity-70' : ''}`}
      data-testid={`auditoria-ia-tarjeta-${item.expediente_id}`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={seleccionada}
            onChange={() => onSeleccionar(item.actuacion_id)}
            className="mt-1 w-4 h-4 accent-blue-600 cursor-pointer"
            aria-label={`Seleccionar auditoría de ${item.numero_expediente}`}
            data-testid={`auditoria-ia-seleccionar-${item.actuacion_id}`}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={`/expedientes/${item.expediente_id}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Abrir el expediente en una pestaña nueva"
                className="font-semibold text-blue-700 hover:text-blue-900 hover:underline transition-colors duration-150 flex items-center gap-1"
                data-testid={`auditoria-ia-ir-expediente-${item.expediente_id}`}
              >
                {item.numero_expediente}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${est.chip}`}>{item.prioridad}</span>
              {item.archivada && (
                <span className="text-[10px] uppercase tracking-wide text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded" data-testid={`auditoria-ia-chip-archivada-${item.actuacion_id}`}>
                  archivada
                </span>
              )}
              {item.visto && !item.archivada && (
                <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded" data-testid={`auditoria-ia-chip-visto-${item.actuacion_id}`}>
                  visto
                </span>
              )}
              {item.expediente_estado && (
                <span className="text-[10px] uppercase tracking-wide text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{item.expediente_estado}</span>
              )}
            </div>
            <p className="text-sm text-gray-700 mt-1 truncate" title={item.expediente_titulo || ''}>{item.expediente_titulo}</p>

            {item.veredicto && (
              <p className="text-sm text-gray-800 mt-2" data-testid={`auditoria-ia-veredicto-${item.expediente_id}`}>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mr-2">Veredicto</span>
                {item.veredicto}
              </p>
            )}
            {item.siguiente_accion && (
              <p className="text-sm text-gray-800 mt-1" data-testid={`auditoria-ia-accion-${item.expediente_id}`}>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mr-2">Siguiente</span>
                {item.siguiente_accion}
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
              <Calendar className="w-3 h-3" />{fechaCorta(item.fecha_auditoria)}
            </p>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {item.descripcion && (
            <button
              type="button"
              onClick={() => setAbierta((v) => !v)}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors duration-150"
              data-testid={`auditoria-ia-ver-informe-${item.expediente_id}`}
            >
              {abierta ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {abierta ? 'Ocultar informe' : 'Ver informe completo'}
            </button>
          )}
          <span className="flex-1" />
          <button
            type="button"
            disabled={ocupado}
            onClick={() => onMarcar([item.actuacion_id], { visto: !item.visto })}
            className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1 disabled:opacity-50"
            data-testid={`auditoria-ia-visto-${item.actuacion_id}`}
          >
            {item.visto ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {item.visto ? 'Marcar sin ver' : 'Marcar visto'}
          </button>
          <button
            type="button"
            disabled={ocupado}
            onClick={() => onMarcar([item.actuacion_id], { archivada: !item.archivada })}
            className="text-xs text-purple-700 hover:text-purple-900 flex items-center gap-1 disabled:opacity-50"
            data-testid={`auditoria-ia-archivar-${item.actuacion_id}`}
          >
            {item.archivada ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
            {item.archivada ? 'Desarchivar' : 'Archivar'}
          </button>
          <button
            type="button"
            disabled={ocupado}
            onClick={() => onBorrar([item.actuacion_id], item.numero_expediente)}
            className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 disabled:opacity-50"
            data-testid={`auditoria-ia-borrar-${item.actuacion_id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />Borrar
          </button>
        </div>
      </div>

      {abierta && item.descripcion && (
        <div className="px-4 pb-4 border-t bg-gray-50" data-color-mode="light">
          <div className="bg-white p-3 rounded border mt-3 prose prose-sm max-w-none">
            <MDEditor.Markdown source={item.descripcion} />
          </div>
        </div>
      )}
    </div>
  );
};

const AuditoriaIAPanel = () => {
  const [vista, setVista] = useState('auditorias');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [texto, setTexto] = useState('');
  const [seleccion, setSeleccion] = useState([]);
  const temporizador = useRef(null);

  const cargar = useCallback(async (f) => {
    setCargando(true);
    setError('');
    try {
      const res = await expedientesAPI.auditoriaIAPanel(f);
      setDatos(res.data || null);
    } catch (e) {
      setError('No se ha podido cargar el panel de auditoría IA.');
    }
    setCargando(false);
  }, []);

  useEffect(() => { if (vista === 'auditorias') cargar(filtros); }, [filtros, cargar, vista]);

  // Buscador con pausa: no dispara una consulta por tecla
  useEffect(() => {
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => {
      setFiltros((f) => (f.buscar === texto ? f : { ...f, buscar: texto, pagina: 1 }));
    }, 400);
    return () => temporizador.current && clearTimeout(temporizador.current);
  }, [texto]);

  const cambiar = (cambios) => { setSeleccion([]); setFiltros((f) => ({ ...f, ...cambios, pagina: cambios.pagina || 1 })); };

  const resultados = datos?.resultados || [];
  const contadores = datos?.contadores || {};
  const prioridades = (datos?.prioridades || Object.keys(ESTILOS).filter((k) => k !== 'Sin clasificar')).concat(['Sin clasificar']);
  const total = datos?.total || 0;
  const paginas = datos?.paginas || 1;

  const alternar = (id) => setSeleccion((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const todasSeleccionadas = resultados.length > 0 && seleccion.length === resultados.length;

  const marcar = async (ids, cambios) => {
    setOcupado(true);
    setAviso('');
    try {
      await expedientesAPI.auditoriaIAMarcar({ actuacion_ids: ids, ...cambios });
      setSeleccion([]);
      await cargar(filtros);
    } catch (e) {
      setError('No se ha podido actualizar la auditoría.');
    }
    setOcupado(false);
  };

  const borrar = async (ids, etiqueta) => {
    const nombre = etiqueta || `${ids.length} auditoría${ids.length === 1 ? '' : 's'}`;
    if (!window.confirm(`Se va a BORRAR la auditoría de ${nombre} del expediente. Esta acción no se puede deshacer. ¿Continuar?`)) return;
    if (ids.length > 1 && !window.confirm(`Confirma otra vez: se borrarán ${ids.length} auditorías.`)) return;
    setOcupado(true);
    try {
      const res = await expedientesAPI.auditoriaIABorrar(ids);
      setAviso(`Borradas ${res.data?.borradas || 0} auditorías.`);
      setSeleccion([]);
      await cargar(filtros);
    } catch (e) {
      setError('No se ha podido borrar.');
    }
    setOcupado(false);
  };

  const limpiarAntiguas = async () => {
    setOcupado(true);
    setAviso('');
    try {
      const previo = await expedientesAPI.auditoriaIAContarAntiguas();
      const n = previo.data?.antiguas || 0;
      if (n === 0) {
        setAviso('No hay auditorías repetidas: cada expediente tiene una sola.');
      } else if (window.confirm(`Se borrarán ${n} auditorías antiguas y se conservará la última de cada expediente. ¿Continuar?`)) {
        const res = await expedientesAPI.auditoriaIALimpiarAntiguas();
        setAviso(`Borradas ${res.data?.borradas || 0} auditorías antiguas.`);
        await cargar(filtros);
      }
    } catch (e) {
      setError('No se han podido limpiar las auditorías antiguas.');
    }
    setOcupado(false);
  };

  const limpiarDuplicadas = async () => {
    setOcupado(true);
    setAviso('');
    try {
      const previo = await expedientesAPI.auditoriaIAContarDuplicadas();
      const n = previo.data?.duplicadas || 0;
      const exps = previo.data?.expedientes || 0;
      if (n === 0) {
        setAviso('No hay auditorías duplicadas: ningún expediente tiene dos del mismo día.');
      } else if (window.confirm(`Se borrarán ${n} auditorías duplicadas (${exps} expediente(s)) y se conservará la última de cada día. ¿Continuar?`)) {
        const res = await expedientesAPI.auditoriaIALimpiarDuplicadas();
        setAviso(`Borradas ${res.data?.borradas || 0} auditorías duplicadas del mismo día.`);
        await cargar(filtros);
      }
    } catch (e) {
      setError('No se han podido quitar las auditorías duplicadas.');
    }
    setOcupado(false);
  };

  const exportar = async (formato) => {
    const { pagina, por_pagina, ...resto } = filtros;
    setOcupado(true);
    setAviso('');
    try {
      const res = await expedientesAPI.auditoriaIAExportar({ ...resto, formato, maximo: 500 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `auditorias_ia_${new Date().toISOString().slice(0, 10)}.${formato === 'md' ? 'md' : 'csv'}`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      window.URL.revokeObjectURL(url);
      setAviso(formato === 'md' ? 'Descargado el informe en Markdown para Claude.' : 'Descargado el CSV.');
    } catch (e) {
      setError('No se ha podido exportar.');
    }
    setOcupado(false);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="auditoria-ia-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-600" />Auditoría IA
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Última revisión del auditor por expediente. Busca, archiva lo revisado y borra lo que ya no sirva.
          </p>
        </div>
        <div className={`items-center gap-2 flex-wrap ${vista === 'auditorias' ? 'flex' : 'hidden'}`}>
          <Button variant="outline" size="sm" onClick={() => exportar('csv')} data-testid="auditoria-ia-exportar-csv">
            <FileSpreadsheet className="w-4 h-4 mr-2" />CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportar('md')} title="Informe completo en Markdown para dárselo a Claude" data-testid="auditoria-ia-exportar-md">
            <FileText className="w-4 h-4 mr-2" />MD para Claude
          </Button>
          <Button variant="outline" size="sm" onClick={limpiarDuplicadas} disabled={ocupado} title="Borra las auditorías repetidas del mismo expediente y el mismo día, conservando la última" data-testid="auditoria-ia-limpiar-duplicadas">
            <Copy className="w-4 h-4 mr-2" />Quitar duplicadas
          </Button>
          <Button variant="outline" size="sm" onClick={limpiarAntiguas} disabled={ocupado} data-testid="auditoria-ia-limpiar-antiguas">
            <Brush className="w-4 h-4 mr-2" />Limpiar antiguas
          </Button>
          <Button variant="outline" size="sm" onClick={() => cargar(filtros)} disabled={cargando} data-testid="auditoria-ia-recargar">
            {cargando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Actualizar
          </Button>
        </div>
      </div>


      <div className="flex items-center gap-2 border-b border-gray-200 mb-5">
        {[['auditorias', 'Auditorías', Sparkles], ['encargos', 'Encargos', ClipboardList], ['borradores', 'Borradores', FileText]].map(([id, etq, Icono]) => (
          <button
            key={id}
            type="button"
            onClick={() => setVista(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 flex items-center gap-2 ${vista === id ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            data-testid={`auditoria-ia-pestana-${id}`}
          >
            <Icono className="w-4 h-4" />{etq}
          </button>
        ))}
      </div>

      {vista === 'encargos' ? <EncargosIAPanel /> : vista === 'borradores' ? <BorradoresIAPanel /> : (<>
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-5 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Buscar por nº de expediente, cliente, veredicto o texto del informe…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="auditoria-ia-buscador"
          />
          {texto && (
            <button type="button" onClick={() => setTexto('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" data-testid="auditoria-ia-limpiar-busqueda">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap text-sm">
          <select value={filtros.dias} onChange={(e) => cambiar({ dias: Number(e.target.value) })} className="border border-gray-200 rounded-md px-2 py-1.5" data-testid="auditoria-ia-filtro-dias">
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 3 meses</option>
            <option value={365}>Último año</option>
            <option value={0}>Todas las fechas</option>
          </select>
          <select value={filtros.archivadas} onChange={(e) => cambiar({ archivadas: e.target.value })} className="border border-gray-200 rounded-md px-2 py-1.5" data-testid="auditoria-ia-filtro-archivadas">
            <option value="no">Sin archivar</option>
            <option value="si">Solo archivadas</option>
            <option value="todas">Todas</option>
          </select>
          <select value={filtros.orden} onChange={(e) => cambiar({ orden: e.target.value })} className="border border-gray-200 rounded-md px-2 py-1.5" data-testid="auditoria-ia-filtro-orden">
            <option value="prioridad">Por prioridad</option>
            <option value="fecha_desc">Más recientes</option>
            <option value="fecha_asc">Más antiguas</option>
            <option value="expediente">Por nº de expediente</option>
          </select>
          <select value={filtros.por_pagina} onChange={(e) => cambiar({ por_pagina: Number(e.target.value) })} className="border border-gray-200 rounded-md px-2 py-1.5" data-testid="auditoria-ia-filtro-por-pagina">
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </select>
          <label className="flex items-center gap-2 text-gray-600 cursor-pointer">
            <input type="checkbox" checked={filtros.solo_no_vistas} onChange={(e) => cambiar({ solo_no_vistas: e.target.checked })} className="w-4 h-4 accent-blue-600" data-testid="auditoria-ia-filtro-no-vistas" />
            Solo sin ver
          </label>
          {(filtros.buscar || filtros.prioridad || filtros.archivadas !== 'no' || filtros.solo_no_vistas || filtros.dias !== 30) && (
            <button type="button" onClick={() => { setTexto(''); setSeleccion([]); setFiltros(FILTROS_INICIALES); }} className="text-xs text-blue-600 hover:text-blue-800" data-testid="auditoria-ia-reset-filtros">
              Quitar filtros
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {prioridades.map((p) => {
          const est = estiloDe(p);
          const Icono = est.icon;
          const activo = filtros.prioridad === p;
          return (
            <button
              key={p}
              type="button"
              onClick={() => cambiar({ prioridad: activo ? '' : p })}
              data-testid={`auditoria-ia-contador-${slug(p)}`}
              className={`text-left bg-white rounded-lg border p-3 transition-all duration-200 hover:shadow-md ${activo ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'}`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${est.punto}`} />
                <Icono className="w-4 h-4 text-gray-400" />
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-1">{contadores[p] || 0}</p>
              <p className="text-[11px] text-gray-500 leading-tight">{p}</p>
            </button>
          );
        })}
      </div>

      {aviso && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 text-sm mb-4" data-testid="auditoria-ia-aviso">{aviso}</div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm mb-4" data-testid="auditoria-ia-error">{error}</div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap mb-3 text-sm text-gray-600">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={todasSeleccionadas}
            onChange={() => setSeleccion(todasSeleccionadas ? [] : resultados.map((r) => r.actuacion_id))}
            className="w-4 h-4 accent-blue-600"
            data-testid="auditoria-ia-seleccionar-todas"
          />
          {seleccion.length > 0 ? `${seleccion.length} seleccionadas` : 'Seleccionar la página'}
        </label>
        <span data-testid="auditoria-ia-total">{total} auditoría{total === 1 ? '' : 's'} · página {filtros.pagina} de {paginas}</span>
      </div>

      {seleccion.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="auditoria-ia-acciones-lote">
          <Button variant="outline" size="sm" disabled={ocupado} onClick={() => marcar(seleccion, { visto: true })} data-testid="auditoria-ia-lote-visto">
            <Eye className="w-4 h-4 mr-2" />Marcar vistas
          </Button>
          <Button variant="outline" size="sm" disabled={ocupado} onClick={() => marcar(seleccion, { archivada: true })} data-testid="auditoria-ia-lote-archivar">
            <Archive className="w-4 h-4 mr-2" />Archivar
          </Button>
          <Button variant="outline" size="sm" disabled={ocupado} onClick={() => marcar(seleccion, { archivada: false })} data-testid="auditoria-ia-lote-desarchivar">
            <ArchiveRestore className="w-4 h-4 mr-2" />Desarchivar
          </Button>
          <Button variant="destructive" size="sm" disabled={ocupado} onClick={() => borrar(seleccion)} data-testid="auditoria-ia-lote-borrar">
            <Trash2 className="w-4 h-4 mr-2" />Borrar
          </Button>
        </div>
      )}

      {!error && cargando && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando auditorías…
        </div>
      )}

      {!error && !cargando && resultados.length === 0 && (
        <div className="bg-white border border-dashed border-gray-300 rounded-lg p-10 text-center" data-testid="auditoria-ia-vacio">
          <Sparkles className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No hay auditorías con estos filtros</p>
          <p className="text-sm text-gray-400 mt-1">Prueba a quitar el filtro de fecha o a buscar por otro número de expediente.</p>
        </div>
      )}

      {!error && !cargando && resultados.length > 0 && (
        <div className="space-y-3" data-testid="auditoria-ia-listado">
          {resultados.map((item) => (
            <TarjetaAuditoria
              key={item.actuacion_id}
              item={item}
              seleccionada={seleccion.includes(item.actuacion_id)}
              onSeleccionar={alternar}
              onMarcar={marcar}
              onBorrar={borrar}
              ocupado={ocupado}
            />
          ))}
        </div>
      )}

      {paginas > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6" data-testid="auditoria-ia-paginacion">
          <Button variant="outline" size="sm" disabled={filtros.pagina <= 1 || cargando}
            onClick={() => setFiltros((f) => ({ ...f, pagina: f.pagina - 1 }))} data-testid="auditoria-ia-pagina-anterior">
            <ChevronLeft className="w-4 h-4 mr-1" />Anterior
          </Button>
          <span className="text-sm text-gray-600">{filtros.pagina} / {paginas}</span>
          <Button variant="outline" size="sm" disabled={filtros.pagina >= paginas || cargando}
            onClick={() => setFiltros((f) => ({ ...f, pagina: f.pagina + 1 }))} data-testid="auditoria-ia-pagina-siguiente">
            Siguiente<ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
      </>)}
    </div>
  );
};

export default AuditoriaIAPanel;
